/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/prop-types */
/*
 * Date range filter popover for the Media Library header.
 * Two react-datepicker fields (From / To) — typeable inputs that open a calendar
 * on focus (selectsStart / selectsEnd) — plus Save/Clear. Popover positioning
 * mirrors FilterMenu (mask + caret, x from the trigger button rect).
 *
 * @param {number} x: horizontal center of the trigger button (from getBoundingClientRect)
 * @param {number|null} dateFrom: inclusive epoch-ms lower bound, or null
 * @param {number|null} dateTo: inclusive epoch-ms upper bound, or null
 * @param {function} onSave: (fromMs, toMs) => void — apply the range
 * @param {function} onClear: () => void — clear the range (popover stays open)
 * @param {function} onClose: () => void — close the popover
 * @param {function} onBack: () => void — close the popover and return to the filter menu
 */
import {useEffect, useRef, useState} from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangeFilter.scss';
import Locale from '../util/Locale';
import Util from '../util/Util';

function localStartOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).getTime();
}

function localEndOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime();
}

function DateRangeFilter({x = 0, dateFrom = null, dateTo = null, onSave, onClear, onClose, onBack}) {
    const ref = useRef(null);
    const [menuPositionStyle, setMenuPositionStyle] = useState({});
    const [caretPositionStyle, setCaretPositionStyle] = useState({});
    const [startDate, setStartDate] = useState(dateFrom ? new Date(dateFrom) : null);
    const [endDate, setEndDate] = useState(dateTo ? new Date(dateTo) : null);
    const today = new Date();

    const setMenuPosition = () => {
        const width = ref.current ? ref.current.clientWidth : 320;
        let menuLeft = x - width / 2;
        if (menuLeft < 0) {
            menuLeft = 5;
        }
        if (menuLeft + width >= window.innerWidth) {
            menuLeft = window.innerWidth - width;
        }
        const caretLeft = Math.abs(x - menuLeft) - 8; // half the caret width
        setCaretPositionStyle({left: `${caretLeft}px`});
        setMenuPositionStyle({left: `${menuLeft}px`});
    };

    useEffect(() => {
        const handleResize = () => onClose && onClose();
        window.addEventListener('resize', handleResize);
        setMenuPosition();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canSave = Boolean(startDate && endDate);

    const handleSave = () => {
        if (!canSave) {
            return;
        }
        // Order the bounds so a typed From > To can't invert the range.
        const [lo, hi] = startDate <= endDate ? [startDate, endDate] : [endDate, startDate];
        if (onSave) {
            onSave(localStartOfDay(lo), localEndOfDay(hi));
        }
        if (onClose) {
            onClose();
        }
    };

    const handleBack = event => {
        event.stopPropagation();
        if (onBack) {
            onBack();
        }
    };

    const handleClear = () => {
        setStartDate(null);
        setEndDate(null);
        if (onClear) {
            onClear();
        }
    };

    return (
        <div className="date-filter-mask" onClick={onClose}>
            <div
                className="date-filter-menu"
                ref={ref}
                style={menuPositionStyle}
                onClick={event => event.stopPropagation()}
            >
                <div className="caret" style={caretPositionStyle} />
                <span
                    className="date-filter-close"
                    role="button"
                    tabIndex={0}
                    aria-label={Locale.getText('CLOSE')}
                    title={Locale.getText('CLOSE')}
                    onClick={handleBack}
                >
                    {Util.getXIcon('#00528A')}
                </span>
                <div className="date-filter-title">{Locale.getText('FILTER_BY_DATE')}</div>
                <div className="date-filter-fields">
                    <div className="date-field">
                        <span className="date-field-label">{Locale.getText('DATE_FROM')}</span>
                        <DatePicker
                            selectsStart
                            selected={startDate}
                            startDate={startDate}
                            endDate={endDate}
                            maxDate={endDate || today}
                            onChange={date => setStartDate(date)}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="mm/dd/yyyy"
                            className="date-field-input"
                        />
                    </div>
                    <div className="date-field">
                        <span className="date-field-label">{Locale.getText('DATE_TO')}</span>
                        <DatePicker
                            selectsEnd
                            selected={endDate}
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            maxDate={today}
                            onChange={date => setEndDate(date)}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="mm/dd/yyyy"
                            className="date-field-input"
                        />
                    </div>
                </div>
                <div className="date-filter-actions">
                    <nsemble-button class="date-filter-clear" onClick={handleClear}>
                        {Locale.getText('CLEAR')}
                    </nsemble-button>
                    <nsemble-button
                        primary
                        class="date-filter-save"
                        onClick={handleSave}
                        disabled={!canSave || undefined}
                    >
                        {Locale.getText('FILTER')}
                    </nsemble-button>
                </div>
            </div>
        </div>
    );
}

export default DateRangeFilter;
